import type { Ticket, TicketsData } from '../lib/types.ts';

/** Canonical group order, matching the BackOffice TicketGroup enum. */
export const TICKET_GROUP_ORDER = ['Adgangsbilletter', 'Glamping', 'Overnatning'] as const;
export type TicketGroupName = (typeof TICKET_GROUP_ORDER)[number];

export interface TicketGroupView {
  readonly group: TicketGroupName;
  readonly tickets: readonly Ticket[];
}

/**
 * Resolve a ticket's display group. Prefers the editor-assigned `group` from tickets.json; falls
 * back to the legacy heuristic (accommodation → Overnatning, /GLAMPING/i → Glamping, else
 * Adgangsbilletter) so older files without the additive fields still render sensibly.
 */
export function resolveTicketGroup(ticket: Ticket, fromAccommodation: boolean): TicketGroupName {
  if (ticket.group && (TICKET_GROUP_ORDER as readonly string[]).includes(ticket.group)) {
    return ticket.group as TicketGroupName;
  }
  if (/GLAMPING/i.test(ticket.name)) return 'Glamping';
  if (fromAccommodation) return 'Overnatning';
  return 'Adgangsbilletter';
}

/**
 * Groups the published tickets into the canonical display groups, ordered by `displayOrder`
 * (falling back to source order), and returns only non-empty groups in canonical order.
 * Pure — only operates on tickets present in tickets.json (the for-sale/sold-out set).
 */
export function groupTicketsForDisplay(tickets: TicketsData): TicketGroupView[] {
  const tagged: Array<{ ticket: Ticket; fromAccommodation: boolean }> = [
    ...tickets.admissionTickets.map(ticket => ({ ticket, fromAccommodation: false })),
    ...tickets.accommodationTickets.map(ticket => ({ ticket, fromAccommodation: true })),
  ];

  const buckets = new Map<TicketGroupName, Array<{ ticket: Ticket; order: number }>>();
  tagged.forEach(({ ticket, fromAccommodation }, index) => {
    const group = resolveTicketGroup(ticket, fromAccommodation);
    const order = ticket.displayOrder ?? 1_000_000 + index;
    const bucket = buckets.get(group) ?? [];
    bucket.push({ ticket, order });
    buckets.set(group, bucket);
  });

  return TICKET_GROUP_ORDER
    .map((group): TicketGroupView | null => {
      const bucket = buckets.get(group);
      if (!bucket || bucket.length === 0) return null;
      const sorted = [...bucket]
        .sort((a, b) => a.order - b.order || a.ticket.name.localeCompare(b.ticket.name, 'da-DK'))
        .map(b => b.ticket);
      return { group, tickets: sorted };
    })
    .filter((g): g is TicketGroupView => g !== null);
}
